import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  HStack,
  Spacer,
  Text,
  useColorModeValue,
  useDisclosure,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import BasicBar from 'components/bars/BasicBar';
import {
  SpaceModuleIndexProps,
  SpaceModuleLinkProps,
  SpaceModuleNavigator,
} from 'components/bars/SpaceModuleNavigator';
import { TopBarButton, TopBarLink } from 'components/bars/TopBarActions';
import {
  InfoIcon,
  NavigateForwardIcon,
  SettingsWheelIcon,
} from 'components/icons';
import SpaceIcon from 'components/SpaceIcon';
import { SpaceInfoModal } from 'components/SpaceInfoModal';
import { useSpaceColor } from 'hooks/useSpaceColor';
import { ScreenWithBar } from 'layouts/ScreenWithBar';
import React, { FC } from 'react';
import { generatePath, Outlet, useLocation } from 'react-router-dom';
import { graphql, RelaySafeProvider, useFragment } from 'utils/graphClient';
import { Layout, useActiveLayout } from 'utils/LayoutProvider';
import {
  FAMILY_TREE_PATH_PREFIX,
  SETTINGS_DEFAULT_PATH,
  SPACE_ATTACHMENTS_PATH_PREFIX,
  SPACE_EVENTS_PATH_PREFIX,
  SPACE_MEMBERS_PATH_PREFIX,
  SPACE_PATH,
  SPACE_SETTINGS_PARAM_NAME,
  SPACE_SETTINGS_PATH,
  SPACE_SETTINGS_SPACEID_PARAM_NAME,
} from 'utils/routes';
import { useFeatureFlag } from 'utils/sessionProvider';
import {
  SpaceLayout_space$data,
  SpaceLayout_space$key,
} from './__generated__/SpaceLayout_space.graphql';
import { ChatButton } from 'components/ChatButton';
import { CenteredSpinner } from 'components/CenteredSpinner';
import { GenericErrorFallback } from 'components/GenericErrorBoundary';
import { orderBy } from 'natural-orderby';
import { notEmpty } from 'utils/NotEmptyFilter';

export interface SpaceLayoutProps {
  space: SpaceLayout_space$key;
}

const fragment = graphql`
  fragment SpaceLayout_space on Space {
    ...SpaceInfoModal_space
    id
    name
    __typename
    canUpdate {
      value
    }
    color {
      light
      dark
    }
    modules {
      id
      __typename
      ... on SpaceActivityModule {
        isSpaceActivityModule: __typename
      }
      ... on SpaceAnnouncementsModule {
        isSpaceAnnouncementsModule: __typename
      }
      ... on SpaceFilesModule {
        isSpaceFilesModule: __typename
      }
      ... on SpaceEventsModule {
        isSpaceEventsModule: __typename
      }
      ... on SpaceMembersModule {
        isSpaceMembersModule: __typename
      }
      ... on SpaceFamilyTreeModule {
        isSpaceFamilyTreeModule: __typename
      }
    }
  }
`;

const LINK_ORDER = [
  'SpaceActivityModule',
  'SpaceAnnouncementsModule',
  'SpaceEventsModule',
  'SpaceFilesModule',
  'SpaceMembersModule',
  'SpaceFamilyTreeModule',
];

export const SpaceLayout: FC<SpaceLayoutProps> = ({ space: _space }) => {
  const space = useFragment(fragment, _space);
  const pathParts = useLocation().pathname.split('/');
  // Show only on /spaces/:spaceID and /spaces/:spaceID/:module, but module not being settings
  const isBarVisible = pathParts.length <= 4;
  const isSettings = pathParts[3] === 'settings';
  const isFamilyTreeEnabled = useFeatureFlag('FEATURE_FAMILY_TREE');
  const activeLayout = useActiveLayout();
  const isMobile = activeLayout !== Layout.DESKTOP;

  const links: (SpaceModuleLinkProps | SpaceModuleIndexProps)[] = orderBy(
    [...space.modules],
    (module) => LINK_ORDER.indexOf(module.__typename),
  )
    .map((module) => {
      if (module.isSpaceActivityModule) {
        return { index: true, label: t`Activity` } as SpaceModuleIndexProps;
      } else if (module.isSpaceMembersModule) {
        return { to: SPACE_MEMBERS_PATH_PREFIX, label: t`Members` };
      } else if (module.isSpaceEventsModule) {
        return { to: SPACE_EVENTS_PATH_PREFIX, label: t`Events` };
      } else if (module.isSpaceFilesModule) {
        return { to: SPACE_ATTACHMENTS_PATH_PREFIX, label: t`Files` };
      } else if (module.isSpaceFamilyTreeModule) {
        if (isFamilyTreeEnabled) {
          return { to: FAMILY_TREE_PATH_PREFIX, label: t`Family Tree` };
        }
      }
      return undefined;
    })
    .filter(notEmpty);

  const bgColor = useColorModeValue('colors.gray.100', 'colors.gray.700');
  const txtColor = useColorModeValue('colors.gray.600', 'white');
  return (
    <ScreenWithBar
      sx={{ '--bar-bg': bgColor || '', '--bar-color': txtColor }}
      bar={
        isBarVisible && [
          <SpaceBar
            isSettings={isSettings}
            key="1"
            space={space}
            links={links}
          />,
        ]
      }
      mobileFlex={isMobile}
    >
      <RelaySafeProvider
        // TODO: Verify if this is the correct error screen
        ErrorComponent={(props) => (
          <GenericErrorFallback {...props} disableBar />
        )}
        LoadingComponent={() => <CenteredSpinner />}
      >
        <Outlet />
      </RelaySafeProvider>
    </ScreenWithBar>
  );
};

const SpaceBar = ({
  space,
  links,
  isSettings,
}: {
  space: SpaceLayout_space$data;
  links: (SpaceModuleLinkProps | SpaceModuleIndexProps)[];
  isSettings: boolean;
}) => {
  const activeLayout = useActiveLayout();
  const isMobile = activeLayout !== Layout.DESKTOP;
  const spaceColor = useSpaceColor(space);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const centerModuleNavigator = !isMobile && !isSettings;
  // TODO: This happens during the delete sequence. Possible relay bug?
  if (!space) return null;

  const { id: spaceId, name, canUpdate } = space;

  return (
    <>
      <SpaceInfoModal
        space={space}
        isOpen={isOpen}
        onClose={onClose}
        disableVisitButton
      />

      <BasicBar>
        <HStack w="100%">
          <Breadcrumb
            data-qa="space-home-breadcrumb"
            separator={<NavigateForwardIcon size="sm" color="gray.500" />}
            alignSelf="left"
            flex={centerModuleNavigator ? 1 : undefined}
          >
            <BreadcrumbItem>
              <BreadcrumbLink to={generatePath(SPACE_PATH, { spaceId })}>
                <SpaceBarHeader name={name} spaceColor={spaceColor || ''} />
              </BreadcrumbLink>
            </BreadcrumbItem>

            {isSettings && (
              <BreadcrumbItem isCurrentPage>
                <BreadcrumbLink
                  to={generatePath(SPACE_SETTINGS_PATH, { spaceId })}
                >{t`Settings`}</BreadcrumbLink>
              </BreadcrumbItem>
            )}
          </Breadcrumb>
          {centerModuleNavigator ? (
            <Box>
              <SpaceModuleNavigator links={links} />
            </Box>
          ) : (
            <Spacer />
          )}

          {!isSettings && (
            <HStack
              flex={centerModuleNavigator ? 1 : undefined}
              justifyContent="flex-end"
            >
              <ChatButton node={space} />
              {canUpdate.value ? (
                <SpaceSettingsButton spaceId={spaceId} />
              ) : (
                <SpaceInfoButton onClick={onOpen} />
              )}
            </HStack>
          )}
        </HStack>
      </BasicBar>

      {isMobile && !isSettings && (
        <BasicBar px={0}>
          <SpaceModuleNavigator links={links} />
        </BasicBar>
      )}
    </>
  );
};

const SpaceSettingsButton = ({ spaceId }: { spaceId: string }) => {
  return (
    <TopBarLink
      ariaLabel={t`Space settings`}
      to={{
        search: new URLSearchParams({
          [SPACE_SETTINGS_SPACEID_PARAM_NAME]: spaceId,
          [SPACE_SETTINGS_PARAM_NAME]: SETTINGS_DEFAULT_PATH,
        }).toString(),
      }}
    >
      <SettingsWheelIcon />
    </TopBarLink>
  );
};

const SpaceInfoButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <TopBarButton ariaLabel={t`Space info`} onClick={onClick}>
      <InfoIcon />
    </TopBarButton>
  );
};

interface SpaceBarHeaderProps {
  name: string;
  spaceColor: string;
}

const SpaceBarHeader = ({ name, spaceColor }: SpaceBarHeaderProps) => {
  return (
    <HStack overflow="hidden" textOverflow="ellipsis">
      <SpaceIcon color={spaceColor} />
      <Text fontWeight="bold" noOfLines={1}>
        {name}
      </Text>
    </HStack>
  );
};
